import { SafeError, SafePromise, safeAwaitAll } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';

import {
  Campaigns,
  InternalNode,
  Author,
  AuthorId,
  NodeId,
  ArticleData,
  TrendingIndiaPosts,
  DraftArticle,
  EditorialArticlePreviewResponse,
  ExpiredUrlResponse,
  RelevantArticlesIds,
  TopStoriesResponse,
  TrendingPostsHomepage,
  WNSTNFollowUpQuestionsResponse,
  ExpiredUrlType,
  RecommendedResponse,
} from './entities/article';
import {
  CampaignRestful,
  ExternalRestful,
  AuthorRestful,
  InternalContentRestful,
  RelevantArticlesRestful,
  RecommendedRestful,
} from './restful';
import { ingestAuthor } from './ingest';
import { Session } from '@benzinga/session';
import { EditorialToolsRestful } from './restful/editorialTools';
import { CommentCountResponse } from './entities';
import { CommentsRestful } from './restful/comments';
import {
  getExpiredUrlSkipReason,
  isExpiredUrlActive,
  normalizeExpiredUrlApiResponse,
  normalizeExpiredUrlSlug,
  skipExpiredUrlFetchResponse,
} from './expiredUrlSlug';
import { TtlFifoCache } from '@benzinga/utils';

const EXPIRED_URL_RESPONSE_CACHE_TTL_MS = 3 * 60 * 60 * 1000;
const EXPIRED_URL_FAIL_OPEN_CACHE_TTL_MS = 600000;
const expiredUrlResponseCache = new TtlFifoCache(3000);
const expiredUrlInFlight = new Map<string, Promise<ExpiredUrlResponse>>();

const FOLLOW_UP_QUESTIONS_CACHE_TTL_MS = 1 * 60 * 60 * 1000;
const FOLLOW_UP_QUESTIONS_CACHE_MAX_ENTRIES = 500;
const followUpQuestionsResponseCache = new TtlFifoCache(FOLLOW_UP_QUESTIONS_CACHE_MAX_ENTRIES);

const RECOMMENDED_ARTICLES_CACHE_TTL_MS = 5 * 60 * 1000;
const RECOMMENDED_ARTICLES_CACHE_MAX_ENTRIES = 1000;
const recommendedArticlesResponseCache = new TtlFifoCache(RECOMMENDED_ARTICLES_CACHE_MAX_ENTRIES);

const shouldLogUpstreamDiagnostics = (): boolean =>
  typeof window === 'undefined' && process.env.BZ_UPSTREAM_REQUEST_STATS === 'true';

const isExpiredUrlNotFoundResponse = (data: Partial<ExpiredUrlResponse> | undefined): data is ExpiredUrlResponse =>
  data?.data === null && data.message === 'Expired URL not found';

const readExpiredUrlNotFoundResponse = async (error: SafeError): Promise<ExpiredUrlResponse | undefined> => {
  if (!(error.data instanceof Response)) return undefined;
  const data = (await error.data
    .clone()
    .json()
    .catch(() => undefined)) as Partial<ExpiredUrlResponse> | undefined;
  if (!isExpiredUrlNotFoundResponse(data)) return undefined;
  return normalizeExpiredUrlApiResponse({
    data: null,
    message: data.message,
    status: data.status === 'error' ? 'error' : 'success',
  });
};

const logExpiredUrlCheckResult = (
  type: ExpiredUrlType,
  slug: string,
  normalizedSlug: string,
  response: ExpiredUrlResponse,
): void => {
  if (!shouldLogUpstreamDiagnostics()) return;
  if (!isExpiredUrlActive(response) && response.message === 'Expired URL not found') {
    return;
  }
  console.warn(
    JSON.stringify({
      contentType: type,
      event: 'expired_url_check_result',
      hasData: Boolean(response.data),
      isExpired: isExpiredUrlActive(response),
      message: response.message,
      normalizedSlug,
      responseDataIsNull: response.data === null,
      slug,
      status: response.status,
      timestamp: new Date().toISOString(),
      type,
    }),
  );
};

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'article:get_author_error'
    | 'article:get_article_error'
    | 'article:get_draft_article_error'
    | 'article:get_trending_india_stories_ids_error'
    | 'article:get_trending_india_topics_error';
  type: 'error';
}

interface AuthorUpdateEvent {
  type: 'article:retrieving_author';
}

interface ArticleUpdateEvent {
  type: 'article:retrieving_article' | 'article:retrieving_draft_article';
}

export type ArticleRequestEvent = ErrorEvent | ArticleUpdateEvent | AuthorUpdateEvent;

export class ArticleRequest extends ListenableSubscribable<ArticleRequestEvent> {
  private authorRestful: AuthorRestful;
  private campaignRequest: CampaignRestful;
  private internalContentRestful: InternalContentRestful;
  private externalRestful: ExternalRestful;
  private relevantArticlesRestful: RelevantArticlesRestful;
  private editorialToolsRestful: EditorialToolsRestful;
  private commentsRestful: CommentsRestful;
  private recommendedRestful: RecommendedRestful;

  constructor(session: Session) {
    super();
    this.authorRestful = new AuthorRestful(session);
    this.campaignRequest = new CampaignRestful(session);
    this.externalRestful = new ExternalRestful(session);
    this.internalContentRestful = new InternalContentRestful(session);
    this.editorialToolsRestful = new EditorialToolsRestful(session);
    this.relevantArticlesRestful = new RelevantArticlesRestful(session);
    this.commentsRestful = new CommentsRestful(session);
    this.recommendedRestful = new RecommendedRestful(session);
  }

  public getCampaigns = async (nodeId: NodeId): SafePromise<Campaigns> => {
    return this.campaignRequest.getCampaigns(nodeId);
  };

  public getAuthorByPath = async (authorSlug: string): SafePromise<Author> => {
    this.dispatch({
      type: 'article:retrieving_author',
    });
    const author = await ingestAuthor(this.authorRestful.getAuthorByPath(authorSlug));
    if (author.err) {
      this.dispatch({
        error: author.err,
        errorType: 'article:get_author_error',
        type: 'error',
      });
    }
    return author;
  };

  public getAuthor = async (authorId: AuthorId): SafePromise<Author> => {
    this.dispatch({
      type: 'article:retrieving_author',
    });
    const author = await ingestAuthor(this.authorRestful.getAuthor(authorId));
    if (author.err) {
      this.dispatch({
        error: author.err,
        errorType: 'article:get_author_error',
        type: 'error',
      });
    }
    return author;
  };

  public getArticle = async (nodeId: NodeId): SafePromise<ArticleData> => {
    this.dispatch({
      type: 'article:retrieving_article',
    });
    const article = await this.externalRestful.getArticle(nodeId);
    if (article.err) {
      this.dispatch({
        error: article.err,
        errorType: 'article:get_article_error',
        type: 'error',
      });
    }
    return article;
  };

  public getDraftArticle = async (nodeId: NodeId, token?: string): SafePromise<DraftArticle> => {
    this.dispatch({
      type: 'article:retrieving_draft_article',
    });
    const article = await this.externalRestful.getDraftArticle(nodeId, token);
    if (article.err) {
      this.dispatch({
        error: article.err,
        errorType: 'article:get_draft_article_error',
        type: 'error',
      });
    }
    return article;
  };

  getEditorialArticlePreview = async (nodeId: number | string): SafePromise<EditorialArticlePreviewResponse> => {
    return await this.editorialToolsRestful.getEditorialArticlePreview(nodeId);
  };

  public getTrendingIndiaStoriesIds = async (): SafePromise<TrendingIndiaPosts[]> => {
    const response = await this.editorialToolsRestful.getTrendingIndiaStoriesIds();
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'article:get_trending_india_stories_ids_error', type: 'error' });
      return response;
    }
    return response;
  };

  public getArticlesWithIds = async (ids: number[]): SafePromise<ArticleData[]> => {
    const reqs = ids.map(id => this.getArticle(id));

    const response = await safeAwaitAll(reqs);

    if (response.err && response.err.length) {
      return { err: response.err[0] };
    }

    if (Array.isArray(response.ok)) {
      return {
        ok: response.ok,
      };
    } else {
      return { ok: [] };
    }
  };

  public getInternalNode = async (nodeId: NodeId): SafePromise<InternalNode> => {
    return this.internalContentRestful.getInternalNode(nodeId);
  };

  public getRelevantArticles = async (channelId = 0): SafePromise<RelevantArticlesIds> => {
    return this.relevantArticlesRestful.getRelevantArticles(channelId);
  };

  getCommentCount = async (id: number): SafePromise<CommentCountResponse | undefined> => {
    return this.commentsRestful.getCommentCount(id);
  };

  getFollowUpQuestions = async (
    nodeId: number | string,
    force?: boolean,
  ): SafePromise<WNSTNFollowUpQuestionsResponse> => {
    const processCacheKey = `followup:${String(nodeId)}`;
    if (!force) {
      const processCached = followUpQuestionsResponseCache.get<WNSTNFollowUpQuestionsResponse>(processCacheKey);
      if (processCached !== undefined) return { ok: processCached };
    }
    const response = await this.editorialToolsRestful.getEditorialFollowUpsQuestions(nodeId);
    if (response.ok) {
      followUpQuestionsResponseCache.set(processCacheKey, response.ok, FOLLOW_UP_QUESTIONS_CACHE_TTL_MS);
    }
    return response;
  };

  checkExpiredUrl = async (type: ExpiredUrlType, slug: string, force?: boolean): SafePromise<ExpiredUrlResponse> => {
    const expiredUrlSkipReason = getExpiredUrlSkipReason(type, slug);
    if (expiredUrlSkipReason !== null) {
      if (shouldLogUpstreamDiagnostics()) {
        const normalizedSlug = normalizeExpiredUrlSlug(type, slug);
        console.warn(
          JSON.stringify({
            contentType: type,
            event: 'expired_url_fetch_skipped',
            normalizedSlug,
            reason: expiredUrlSkipReason,
            slug,
            slugLength: slug.length,
            timestamp: new Date().toISOString(),
            type,
          }),
        );
      }
      return { ok: skipExpiredUrlFetchResponse() };
    }
    const normalizedSlug = normalizeExpiredUrlSlug(type, slug);
    const processCacheKey = `${type}:${normalizedSlug}`;
    if (!force) {
      const processCached = expiredUrlResponseCache.get<ExpiredUrlResponse>(processCacheKey);
      if (processCached !== undefined) {
        return { ok: processCached };
      }
    }
    const inFlight = expiredUrlInFlight.get(processCacheKey);
    if (inFlight) {
      return { ok: await inFlight };
    }

    const fetchExpiredUrl = async (): Promise<ExpiredUrlResponse> => {
      const response = await this.editorialToolsRestful.checkExpiredUrl(type, normalizedSlug);
      if (response.ok) {
        const normalized = normalizeExpiredUrlApiResponse(response.ok);
        logExpiredUrlCheckResult(type, slug, normalizedSlug, normalized);
        expiredUrlResponseCache.set(processCacheKey, normalized, EXPIRED_URL_RESPONSE_CACHE_TTL_MS);
        return normalized;
      }
      const notFoundResponse = await readExpiredUrlNotFoundResponse(response.err);
      if (notFoundResponse) {
        logExpiredUrlCheckResult(type, slug, normalizedSlug, notFoundResponse);
        expiredUrlResponseCache.set(processCacheKey, notFoundResponse, EXPIRED_URL_RESPONSE_CACHE_TTL_MS);
        return notFoundResponse;
      }
      const failOpenResponse = skipExpiredUrlFetchResponse();
      expiredUrlResponseCache.set(processCacheKey, failOpenResponse, EXPIRED_URL_FAIL_OPEN_CACHE_TTL_MS);
      if (shouldLogUpstreamDiagnostics()) {
        console.warn(
          JSON.stringify({
            contentType: type,
            errorMessage: response.err.message,
            errorType: response.err.type,
            event: 'expired_url_check_fail_open',
            normalizedSlug,
            slug,
            timestamp: new Date().toISOString(),
            type,
          }),
        );
      }
      return failOpenResponse;
    };

    const promise = fetchExpiredUrl();
    expiredUrlInFlight.set(processCacheKey, promise);
    try {
      return { ok: await promise };
    } finally {
      expiredUrlInFlight.delete(processCacheKey);
    }
  };

  getRecommendedArticles = async (
    nid: number | string,
    userId?: string,
    limit = 4,
  ): SafePromise<RecommendedResponse> => {
    const cacheKey = `recommended:${nid}:${limit}`;
    const cached = recommendedArticlesResponseCache.get<RecommendedResponse>(cacheKey);
    if (cached !== undefined) return { ok: cached };
    const response = await this.recommendedRestful.getRecommendedArticles(nid, userId ?? '', limit);
    if (response.ok) {
      recommendedArticlesResponseCache.set(cacheKey, response.ok, RECOMMENDED_ARTICLES_CACHE_TTL_MS);
    }
    return response;
  };

  getTrendingPostsByTermId = async (id: string): SafePromise<TopStoriesResponse> => {
    return this.editorialToolsRestful.getTrendingPostsByTermId(id);
  };

  getTrendingPostsByTermName = async (name: string): SafePromise<TopStoriesResponse> => {
    return this.editorialToolsRestful.getTrendingPostsByTermName(name);
  };

  getTrendingStoriesFromEditorial = async (): SafePromise<TrendingPostsHomepage> => {
    return this.editorialToolsRestful.getTrendingStoriesFromEditorial();
  };
}
